// core
import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import moment from 'moment';

// campfire
import { PartnerBrandService } from '@campfire-nx/api';

// Raptor
import { RAPTOR_MEDICAL_VALUE_KEY, RAPTOR_COLUMN_LAYOUT_CLASSES, RAPTOR_COUNTRY_CODES, RAPTOR_ARRANGEMENT_TAG } from '../constant/raptor-constants';
import { RaptorMultichoiceService } from '../services/raptor-multichoice.service';
import { RaptorQuestionService } from '../services/raptor-question.service';
import { RaptorPriceService } from './raptor-price.service';
import { RaptorPredicateService } from './raptor-predicate.service';

interface IDisclaimer {
  content: string;
  type: 'promotional' | 'additionalCharges';
}

interface IBanner {
  content: string;
  type: 'promotional' | 'additionalCharges' | 'banner';
}

export interface IIncludedExtrasDisclaimer {
  disclaimer: string;
  label: string;
}

export interface IArrangementFeeBanner {
  monthlyHeading: string;
  disclaimer: string;
  tag: { 'monthly': string; 'annual': string };
};

@Injectable()
export class RaptorDataService {
  disclaimers: Array<IDisclaimer> = [];
  banners: Array<IBanner> = [];

  constructor(
    private raptorMultichoiceService: RaptorMultichoiceService,
    private raptorQuestionService: RaptorQuestionService,
    private raptorPriceService: RaptorPriceService,
    private raptorPredicateService: RaptorPredicateService,
    private datePipe: DatePipe,
    private partnerBrandService: PartnerBrandService
  ) { }

  /**
   * @ngdoc method
   * @name isDataDefined
   * @methodOf RaptorDataService
   * @description Checks if the provided data is defined.
   * @param {any} data - The data to be checked.
   * @returns {boolean} Returns true if the data is defined, false otherwise.
   */
  isDataDefined(data: any): boolean {
    return data !== null && data !== undefined;
  }

  /**
   * @ngdoc method
   * @name convertToDateObject
   * @methodOf RaptorDataService
   * @description Converts a string date in the format 'YYYY-MM-DD' to a Date object.
   * @param {string} dateString - The date string to be converted.
   * @returns {Date} The converted Date object.
   */
  public convertToDateObject(data: any): any {
    let date;
    let dateObject;

    if (typeof data === 'string') {
      date = data.split('-');
      dateObject = { day: date[2], month: date[1], year: date[0] };
    } else {
      dateObject = data;
    }
    return dateObject;
  }

  /**
   *
   * @param data
   * @returns Object - Date object with day, month, year or raw data in YYYY-MM-DD
   * @description Helper function that return Date to DD-MM-YYYY
   * @todo This function needs regex validation to figure out the format of the date string
   */
  public convertToDateString(data: any) {
    let date;
    let formatDated;

    if (typeof data === 'object' && data.day && data.month && data.year) {
      date = {day: data.day, month: data.month, year: data.year};
    } else if (typeof data === 'string') {
      const dateArray = data.split('-');
      // Check date in YYYY-MM-DD
      if (dateArray.length === 3 && dateArray[0].length === 4) {
        date = {
          day: parseInt(dateArray[2]),
          month: parseInt(dateArray[1]),
          year: parseInt(dateArray[0])
        };
      }
    }

    if (date) {
      formatDated = this.datePipe.transform(new Date(date.year, date.month - 1, date.day), 'dd LLL yyyy');
    } 
    return formatDated;
  }

  /**
   *
   * @param formData
   * @returns Array - of question data
   * @description Helper function that gets all questions declared in the form data that are bound to the form group
   */
  public getAllFormInputs(formData: any) {
    return this.raptorQuestionService.getAllQuestions(formData)
      .filter((question: any) => {
        return question.layout !== 'review' && question.key;
      });
  }

  /**
   *
   * @param formData
   * @returns Array - of question data
   * @description Helper function that prepares all the input fields for the control service.
   * This function updates the formData object by adding to each question the data, and human values.
   * It also dynamically changes the prompt and subtext for each question based on the config.
   * It then return all the questions as a flat array excluding the review questions for the control service
   * in order to make controls from them.
   */
  public mapAllQuestions(formData: any): Array<any> {
    const formQuestions = this.raptorQuestionService.getAllQuestions(formData);

    formQuestions.forEach((question: any) => {
      // add the data
      if (formData.data && this.isDataDefined(formData.data[question.key])) {
        const data = formData.data[question.key];
        if (question.type === 'repeater') {
          question.data = this.mapRepeaterData(question.key, data);
        } else if (question.type === 'date' && question.layout === 'range') {
          question.data = {
            start: data,
            end: formData.data[question.settings?.end_date]
          };
        } else {
          question.data = data;
        }

        // add other question data (Currently we don't handle this if the bupa member is in a repeater)
        if (question.type === 'validation' && question.layout === 'bupa_mem_num') {
          question.bupaMemberType = formData.data.bupa_member_type;
        }
      }

      if (question.type === 'payment' && formData.data?.const_is_staff) {
        question.const_is_staff = true;
      }

      if (question.type === 'multiple_choice') {
        this.raptorMultichoiceService.setNoneData(question, formData);
      }

      // add the label
      if (formData.human && this.isDataDefined(formData.human[question.key])) {
        const human = formData.human[question.key];
        if (question.type === 'repeater') {
          question.human = this.mapRepeaterData(question.key, human);
        } else if (question.type === 'date' && question.layout === 'range') {
          question.human = {
            start: human,
            end: formData.human[question.settings?.end_date]
          };
        } else {
          question.human = human;
        }
      }

      // set repeater multiple questions
      if (question.type === 'repeater' && question.layout === 'multiple') {
        this.mapMutlipleElements(question);
      }

      // override the question prompt and subtext if there is an override in the settings
      // this also happens for the multiple repeater questions. Not yet for the regular repeater
      // until I figure out how I'm going to do it
      if (question.settings) {
        question.prompt = this.replaceQuestionLabel('prompt', question, formData.data, formData.human);
        question.subtext = this.replaceQuestionLabel('subtext', question, formData.data, formData.human);
      }
    });

    return formQuestions
      .filter((question: any) => {
        return question.layout !== 'review' && question.key;
      });
  }

  /**
   *
   * @param columns Array - definition of the layout
   * @param rows Array - the questions in the section
   * @param elements Array - the questions in the section
   * @param components Array - the questions in the section
   * @param parentKey String - parent question for repeaters
   * @returns Object - number of columns and the sorted questions
   */
  public columnLayout(columns: Array<any> | null | undefined, rows: Array<any> | null | undefined, elements: Array<any>, components: any, parentKey?: string) {
    let questions: any = [];
    let numberOfColumns = 1;
    let columnLength = 0;
    let formattedRows: Array<any> = [];

    // The original column implementation allowed a second empty column to signal that the
    // first column would behave as a 2 column layout even though the second column was empty.
    // The old layout would transform both columns into a single array and then the template
    // would then layout it out as multiple columns.
    // This function allows flows to continue to expct this behaviour by mapping this
    // structure into a true 2 column layout until we are asked to remove it.
    // e.g.
    // "columns": [
    //     [
    //         "policy_holder_given_name",
    //         "policy_holder_last_name",
    //         "policy_holder_dob"
    //     ],
    //     []
    // ]
    if (columns && Array.isArray(columns) && columns.length === 2) {
      if (columns[0].length > 0 && columns[1].length === 0) {
        let newColumns: any = [[], []];
        columns[0].forEach((item: any, index: number) => {
          if (index % 2 === 0) {
            newColumns[0].push(item);
          } else {
            newColumns[1].push(item);
          }
        });
        columns = newColumns;
      }
    }

    if (rows && Array.isArray(rows) && rows.length) {
      let columnLength: number = 0;
      // create the formattedRows object from the rows definition
      rows.forEach((row: any, index: number) => {
        let rowColumns = 0;
        // unlike the columns definition the rows must be a 2 dimensional array
        formattedRows[index] = [];

        if (Array.isArray(row)) {
          row.forEach((item: any) => {
            let rowItem;

            if (item && typeof item === 'object' && item.key) {
              rowItem = getItem(item.key);
              rowItem.colspan = item.colspan || 1;
            } else {
              rowItem = getItem(item);
              rowItem.colspan = 1;
            }
            if (rowItem) {
              formattedRows[index].push(rowItem);
            }
            rowColumns = rowColumns + rowItem.colspan;
          });
          columnLength = rowColumns > columnLength ? rowColumns : columnLength;
        }
      });
      numberOfColumns = columnLength;
    } else if (columns && Array.isArray(columns) && columns.length) {
      columns.forEach((column: any, index: number) => {
        if (Array.isArray(column)) {
          numberOfColumns = columns.length;
          // this is a column so loop through it and construct the elements
          questions[index] = [];
          columnLength = column.length > columnLength ? column.length : columnLength;

          column.forEach((item: any) => {
            let columnItem = getItem(item);
            if (columnItem) {
              questions[index].push(columnItem);
            }
          });
        } else {
          questions[index] = questions[index] || [];
          let columnItem = getItem(column);
          if (columnItem) {
            questions[index].push(columnItem);
            columnLength = questions[index].length > columnLength ? questions[index].length : columnLength;
          }
        }
      }); 
    } else if (elements && elements.length) {
      columnLength = elements.length;
      questions = [elements];
    } else {
      questions = [];
      columnLength = 1;
    }

    // Reorganise the columns into rows for a better layout in the template
    for (let i = 0; i < columnLength; i++) {
      let row: Array<any> = [];
      
      questions.forEach((column: any, index: number) => {
        if (column[i]) {
          // all column items have a colspan of 1 and cannot be configured otherwise
          if (typeof column[i] === 'object') {
            column[i].colspan = 1;
          }
          row.push(column[i]);
        } else {
          row.push({ colspan: 1 });
        }
      });
      formattedRows.push(row);
    }

    // add the id question to the column layout if it is not defined in the columns
    // The frontend now expects id question in each repeater
    // this block should only run from the mapMutlipleElements function which is initialising
    // the repeater questions. The functions that just do layout should not enter this block
    // as the id question is not involved in the layout, this is purely to make sure the 
    // id controls are bound to the form as I cannot rely on the configuration of the columns
    // in admin to include the id question
    if (parentKey) {
      if (!questions.find((question: any) => question && question.key === parentKey + '__id')) {
        let idQuestion = elements.find((element: any) => element && element.key === parentKey + '__id');
        if (idQuestion) {
          // add a new row and push the id question onto that new empty row
          // push the id question onto the first column in the layout
          formattedRows.push([idQuestion]);
        }
      }
    }

    function getItem(key: string) {
      let question = elements?.find((question: any) => question && question.key === key);
      let component = components?.find((component: any) => component && component.uuid === key);
      let item;

      // filter out hidden questions
      if (question && question.hidden !== true) {
        item = question;
      } else if (component) {
        item = component;
      } else if (!item) {
        item = {};
      }
      return item;
    }
    
    return {
      questions: formattedRows,
      columns: numberOfColumns
    };
  }

  public columnLayoutClass(columns: number, item: any) {
    let baseClasses = 'cell cell-small-col-span-6 column-layout-' + columns;
    let columnClasses = RAPTOR_COLUMN_LAYOUT_CLASSES.columns[columns -1][item.colspan - 1];
    return baseClasses + ' ' + columnClasses;
  }

  /**
   *
   * @param value any - question value
   * @param question any - question object
   * @returns payload - formatted value for the API
   */
  public createQuestionPayload(value: any, question: any): any {
    let payload = value;

    if (!question) {
      return;
    }

    if (question.type === 'number' && typeof payload === 'string') {
      if (question.settings?.format === 'currency') {
        payload = parseFloat(payload.replace(/[^0-9.]/g, '').replace(/^0+/, ''));
      } else {
        payload = parseFloat(payload.replace(/,/g, ''));
      }
    }

    if (question.type === 'currency' && typeof payload === 'string') {
      payload = parseFloat(payload.replace(/[^0-9.]/g, '').replace(/^0+/, ''));
    }

    if (question.type === 'date' && value) {
      let date;

      if (question.layout === 'range') {
        value = value.start;
      }
      if (typeof value === 'string') {
        date = {
          year: new Date(value).getFullYear(),
          month: new Date(value).getMonth() + 1,
          day: new Date(value).getDate()
        }
      } else if (value._isAMomentObject) {
        date = {
          year: value._i.year,
          month: value._i.month + 1,
          day: value._i.date
        }
      } else if (value.day && value.month && value.year) {
        date = value;
      }

      if (date) {
        date.day = typeof date.day === 'number' && date.day < 10 ? '0' + date.day : date.day;
        date.month = typeof date.month === 'number' && date.month < 10 ? '0' + date.month : date.month;
        payload = [date.year, date.month, date.day].join('-');
      } else {
        payload = null;
      }
    }

    if (question.type === 'repeater') {
      payload = this.createRepeaterPayload(question);
    }

    if (question.type === 'multiple_choice') {
      payload = this.raptorMultichoiceService.createPayload(value);
    }

    if (question.type === 'autocomplete') {
      payload = (value && value.value) || '';
    }

    if (question.type === 'autocomplete_multiple') {
      payload = '';
      if (value && value.value) {
        payload = value.value.map((option: any) => option.value);
      }
    }

    if (question.type === 'payment') {
      payload = {
        account: question.account,
        token: question.token
      };
    }

    return payload;
  }

  /**
   *
   * @param question
   * @returns payload - payload for a repeater question
   */
  public createRepeaterPayload(question: any): any {
    let payload: any[] = [];
    const fields = question.elements && question.elements.flat();

    if (question.data && question.data.length) {
      question.data.forEach((row: any, rowIndex: number) => {
        let element;

        payload[rowIndex] = {};

        // extra logic to handle 2 questions with the same key in show_if logic
        for (const key in row) {
          const elements = fields.find((element: { key: string; }) => element.key === key);

          if (elements && elements.length) {
            if (elements.length > 1) {
              // find the visible element, assume there is a show_if on each of these
              // this is because we can have multiple questions with the same key inside of a repeater
              element = elements.find((element: any) => {
                if (element.show_if && typeof element.show_if === 'object') {
                  return this.raptorPredicateService.validator(element.show_if, row);
                } else {
                  return element;
                }
              });
            } else if (question.elements.length === 1) {
              element = elements[0];
            }
          } else {
            element = elements;
          }

          const repeaterDataKey = key.replace(`${question.key}__`, '');
          payload[rowIndex][repeaterDataKey] = this.createQuestionPayload(row[key], element);
        }
      });
    }
    return payload;
  }

  /**
   * @ngdoc method
   * @name mapFormData
   * @methodOf RaptorDataService
   * @description Helper function that set repeater child questions key context to repeater question
   * @param {any} data - The data to be mapped.
   * @returns {FormData} The mapped FormData object.
   */
  public mapFormData(formData: any) {
    const sections = formData.sections;
    const sidebar = formData.sidebar;

    // check for multi-product covers page and then add in the product_slug question????
    if (formData.layout === 'hollard-car-compare-prices') {
      this.mapCoversPage(formData);
    }

    if (sections && sections.length) {
      sections.forEach((section: any) => {
        if (section.elements && section.elements.length) {
          section.elements.forEach((question: any) => {
            if (
              question.type === 'repeater' &&
              question.elements &&
              question.elements.length
            ) {
              this.mapRepeaters(formData, question);
            }

            if (question.type === 'medical_screening') {
              this.mapMedicalQuestion(formData, question, formData.data);
            }

            // map payment questions here
            if (question.type === 'payment') {
              this.mapPaymentOptions(question);
            }

            if (question.type === 'multiple_choice') {
              this.raptorMultichoiceService.mapNoneValue(question);
            }

            if (question.type === 'extras') {
              this.mapExtras(formData, question);
            }

            if (question.type === 'date') {
              if (question.layout === 'date_select_month_year') {
                question.settings = this.mapMonthYearChoices(question);
              }
              if (question.layout === 'range' && question.settings?.end_date) {
                // get the end date field
                let endDate = this.raptorQuestionService.getQuestionByKey(formData, question.settings?.end_date);
                this.raptorQuestionService.setQuestionAtribute(formData, endDate.key, 'hidden', true);
                question.validations = question.validations || [];
                if (endDate.validations && endDate.validations.length) {
                  question.validations = question.validations.concat(endDate.validations);
                }
              }
            }

            if (
              question.settings &&
              question.settings.show_page_error &&
              formData.errors && formData.errors[question.key]
            ) {
              question.error = formData.errors[question.key];
              delete formData.errors[question.key];
              if (!formData.errors.hasOwnProperty()) {
                delete formData.errors;
              }
            }

            // hide the submit button only when the question is empty
            if (question.settings?.submitOnChange && !formData.data[question.key]) {
              formData.settings.hide_cta = true;
            }
          });
        }
        // map section errors
        if (formData.errors && Object.keys(formData.errors).length > 0) {
          for (let key in formData.errors) {
            if (key === section.uuid) {
              section.errors = section.errors || [];
              section.errors = section.errors.concat(formData.errors[key]);
              delete formData.errors[key];
            } 
          } 
        }
      });
    }

    if (sidebar && sidebar.sections && sidebar.sections.length) {
      this.mapSideBar(sidebar, formData);
    }

    if (formData.errors && Object.keys(formData.errors).length > 0) {
      this.mapErrors(formData);
    } else {
      delete formData.errors;
    }
  }

  /**
   * @ngdoc method
   * @name mapCoversPage
   * @methodOf RaptorDataService
   * @description Maps the provided data to a covers page object.
   * @param {any} data - The data to be mapped.
   * @returns {any} The mapped covers page object.
   */
  private mapCoversPage(formData: any) {
    const sections = formData.sections;

    if (sections && sections.length) {
      sections.forEach((section: any) => {
        if (section.elements && section.elements.length) {
          section.elements.forEach((question: any) => {
            if (question.key === 'cover_type') {
              // append the product_slug question to the section
              section.elements.push({
                key: 'product_slug',
                prompt: '',
                required: true,
                type: 'string',
                uuid: 'product_slug'
              });
            }
          });
        }
      });
    }
  }

  /**
   * @ngdoc method
   * @name mapRepeaterData
   * @methodOf RaptorDataService
   * @description Maps the provided data to a repeater data object.
   * @param {any} data - The data to be mapped.
   * @returns {any} The mapped repeater data object.
   */
  private mapRepeaterData(key: string, data: Array<any>) {
    return data.map((item: any) => {
      const mappedItem: any = {};
      for (const childKey in item) {
        mappedItem[`${key}__${childKey}`] = item[childKey];
      }
      return mappedItem;
    });
  }

  /**
   * @ngdoc method
   * @name mapMutlipleElements
   * @methodOf RaptorDataService
   * @description This maps the repater multiple feilds with key indexing in order to make unique form controls.
   * predicates also need to be updated with new keys if they are questions inside the parent
   * @param {any} data - The data to be mapped.
   * @returns {any} The mapped multiple elements.
   */
  private mapMutlipleElements(question: any) {
    // do the layout config here in order to create questions from the saved data
    let layoutConfig = this.columnLayout(question.settings?.columns, question.settings?.rows, question.elements, question.settings?.components, question.key);
    // assign the columns here so the template knows how many there will be as we do not
    // run the column layout check in the component
    question.columns = layoutConfig.columns;
    // reassign the question elements with the correct column layout in order to make copies when adding new fields
    question.elements = [];
    layoutConfig.questions.forEach((column: any, index: number) => {
      question.elements[index] = [];
      column.forEach((item: any) => {
        // make a deep copy of the item (question/component/null) value
        let newQuestion = JSON.parse(JSON.stringify(item));
        question.elements[index].push(newQuestion);
      });
    });
    question.fields = [];

    if (question.data && question.data.length) {
      question.data.forEach((groupItem: any, index: number) => {
        let columnGroup: Array<any> = [];
        question.elements.forEach((column: any) => {
          let fieldGroup: Array<any> = [];
          column.forEach((field: any) => {

            let item = JSON.parse(JSON.stringify(field));
            // make a deep copy because I don't want the original element mutated
            if (field && field.type && field.uuid) {
              
              item.key = item.key + '_index' + index;
              item.data = groupItem[field.key];

              if (question.human && question.human[index]) {
                item.human = question.human[index][field.key];
              }

              if (item.type === 'medical_screening') {
                item.conditions = groupItem[field.key + RAPTOR_MEDICAL_VALUE_KEY];
              }

              if (
                field.settings &&
                question.data &&
                question.data[index] &&
                question.human &&
                question.human[index]
              ) {
                item.prompt = this.replaceQuestionLabel('prompt', field, question.data[index], question.human[index], question.key);
                item.subtext = this.replaceQuestionLabel('subtext', field, question.data[index], question.human[index], question.key);
              }
              // we now support show_if logic for questions in repeaters, so those predicates also need to be indexed
              // for the repeater multiple questions
              if (item.show_if && item.show_if.operands) {
                item.show_if.operands = this.appendOperandKey(field.show_if.operands, index, question.key);
              }
              if (item.validations && item.validations.length) {
                item.validations.forEach((validation: any) => {
                  if (validation.predicate && validation.predicate.operands) {
                    validation.predicate.operands = this.appendOperandKey(validation.predicate.operands, index, question.key);
                  }
                });
              }
            }
            fieldGroup.push(item);
          });
          columnGroup.push(fieldGroup);
        });
        question.fields.push(columnGroup);  
      });
    }
  }

  /**
   * @ngdoc method
   * @name appendOperandKey
   * @methodOf RaptorDataService
   * @description Appends an operand key to the provided data.
   * @param {any} data - The data to which the operand key will be appended.
   * @returns {any} The data with the appended operand key.
   */
  public appendOperandKey(operands: any, index: number, parentKey: string) {
    let indexedOperands = operands;

    operands.forEach((operand: any, operandIndex: number) => {
      if (typeof operand === 'string') {
        if (operand[0] === '<' && operand[operand.length -1] === '>') {
          const indexRegex = /_index[0-9]+/;
          let isParentElement;
          // pull the pipe out from question key
          let operandPieces = operand.substring(1, operand.length - 1).split('|');

          // strip indexes out before adding the new ones, this happens when adding a new set of questions
          operandPieces[0] = operandPieces[0].replace(indexRegex, '');
          isParentElement = (operandPieces[0] + '__').indexOf(parentKey) > -1;
          indexedOperands[operandIndex] = [
            '<',
            operandPieces[0],
            isParentElement ? '_index' + index : '',
            operandPieces[1] ? '|' + operandPieces[1] : '',
            '>'
          ].join('');
        }
      } else if (typeof operand === 'number' || typeof operand === 'boolean') {
        // not sure if I want to do anything here, leaving it in for now as it mirrors the predicate service logic
      } else if (Array.isArray(operand)) {
        // not sure if I want to do anything here, leaving it in for now as it mirrors the predicate service logic
      } else if (typeof operand === 'object') {
        if (operand.operands) {
          this.appendOperandKey(operand.operands, index, parentKey);
        }
      }
    });

    return indexedOperands;
  }

  /**
   * @ngdoc method
   * @name mapRepeaters
   * @methodOf RaptorDataService
   * @description Maps the repeaters in the provided question.
   * @param {any} question - The question containing repeaters to be mapped.
   */
  private mapRepeaters(formData: any, question: any) {
    const questionSettings = question.settings;

    if (question.elements && question.elements.length) {
      question.elements.forEach((repeaterQuestion: any, index: number) => {
        repeaterQuestion.key = `${question.key}__${repeaterQuestion.key}`;
        if (repeaterQuestion.type === 'medical_screening') {
          let data = formData.data && formData.data[question.key] && formData.data[question.key][index];
          this.mapMedicalQuestion(formData, repeaterQuestion, data);
        }
      });

      if (questionSettings) {
        this.mapRepeaterTableColumns(question);
        this.mapRepeaterLayout(question, 'columns');
        this.mapRepeaterLayout(question, 'rows');
      }
    }
  }

  /**
   * @ngdoc method
   * @name mapRepeaterTableColumns
   * @methodOf RaptorDataService
   * @description Maps the columns of a repeater table.
   * @param {any} data - The data containing the repeater table to be mapped.
   * @returns {any} The mapped repeater table columns.
   */
  private mapRepeaterTableColumns(question: any) {
    if (question.settings.table_columns_order && question.settings.table_columns_order.length) {
      const mappedTableColumns = question.settings.table_columns_order.map((column: any) => {
        // Only do the mapping for items in the table column order that are questions
        let isQuestion = question.elements.find((element: any) => {
          return element.key === `${question.key}__${column}`;
        });
        if (isQuestion) {
          return `${question.key}__${column}`;
        } else {
          return column;
        }
      });
      question.settings.table_columns_order = mappedTableColumns;
    }
  }

  /**
   * @ngdoc method
   * @name mapRepeaterLayout
   * @methodOf RaptorDataService
   * @description Maps the columns and rows of a repeater.
   * @param {any} data - The data containing the repeater to be mapped.
   * @param {string} type - "columns" or "rows"
   * @returns {any} The mapped repeater layout
   */

  private mapRepeaterLayout(question: any, type: string) {
    if (question.settings && Array.isArray(question.settings[type]) && question.settings[type].length) {
      const mappedLayout = question.settings[type].map((layout: any) => {
        // whiat is the test for an object here? is it just another column?
        if (typeof layout === 'object') {
          return layout.map((innerLayout: any) => {
            if (isQuestion(innerLayout, question.elements)) {
              if (innerLayout.key) {
                innerLayout.key = `${question.key}__${innerLayout.key}`;
                return innerLayout;
              } else {
                return `${question.key}__${innerLayout}`;
              }
            } else {
              return innerLayout;
            }
          });
        } else {
          if (isQuestion(layout, question.elements)) {
            return `${question.key}__${layout}`;
          } else {
            return layout;
          }
        }
      });
      question.settings[type] = mappedLayout;
    }

    // Only do the mapping for items in the column order that are questions
    function isQuestion(key: any, elements: any) {
      if (key && typeof key === 'object' && key.key) {
        return elements.find((element:any) => {
          return element.key === `${question.key}__${key.key}`;
        });
      } else {
        return elements.find((element:any) => {
          return element.key === `${question.key}__${key}`;
        });
      }
    }
  }

  /**
   * @ngdoc method
   * @name mapSideBar
   * @methodOf RaptorDataService
   * @description Maps the sidebar data.
   * @param {any} data - The data containing the sidebar to be mapped.
   * @returns {any} The mapped sidebar data.
   */
  private mapSideBar(sidebar: any, formData: any) {
    const sections = sidebar.sections;
    if (sections && sections.length) {
      sections.forEach((section: any) => {
        const settings = section.settings;
        if (settings && settings.groups && settings.groups.length) {
          settings.groups.forEach((group: any) => {
            if (group.component && group.component === 'cta') {
              this.mapActions(group, formData.actions);
            }
          });
        }
        // map section errors
        if (formData.errors && Object.keys(formData.errors).length > 0) {
          for (let key in formData.errors) {
            if (key === section.uuid) {
              section.errors = section.errors || [];
              section.errors = section.errors.concat(formData.errors[key]);
              delete formData.errors[key];
            } 
          } 
        }
      });
    }
  }

  /**
   * @ngdoc method
   * @name mapActions
   * @methodOf RaptorDataService
   * @description Maps the actions data.
   * @param {any} data - The data containing the actions to be mapped.
   * @returns {any} The mapped actions data.
   */
  private mapActions(component: any, actions: any) {
    if (component.settings && component.settings.action && actions && actions[component.settings.action]) {
      component.settings.action_details = actions[component.settings.action];
    }
  }

  /**
   * @ngdoc method
   * @name mapErrors
   * @methodOf RaptorDataService
   * @description Maps the error messages.
   * @param {any} data - The data containing the errors to be mapped.
   * @returns {any} The mapped error messages.
   */
  private mapErrors(formData: any) {
    let mappedErrors = [];
    for (let key in formData.errors) {
      let question = this.raptorQuestionService.getQuestionByKey(formData, key);
      // repeater error message mapping
      if (question && question.type === 'repeater' && formData.errors[key] && typeof formData.errors[key] === 'object') {
        for (let row in formData.errors[key]) {
          let errorBlock: any = formData.errors[key][row];

          if (errorBlock && typeof errorBlock === 'object') {
            for (let itemIndex in errorBlock) {

              if (errorBlock[itemIndex] && typeof errorBlock[itemIndex] === 'object' && question.elements?.length) {
                for (let elementKey in errorBlock[itemIndex]) {
                  let repeaterQuestion = question.elements.find((element: any) => { 
                    return element.key === `${question.key}__${elementKey}`; 
                  });

                  const errorItem = {
                    label: [(question && question.prompt || ''), (repeaterQuestion && repeaterQuestion.prompt || '')].join(' '),
                    key,
                    messages: typeof errorBlock[itemIndex][elementKey][elementKey] === 'string' ?
                      [errorBlock[itemIndex][elementKey][elementKey]] : [...errorBlock[itemIndex][elementKey][elementKey]]
                  }
                  mappedErrors.push(errorItem);
                }
              }
            }
          }
        }
      } else {
        const errorItem = {
          label: question && question.prompt || '',
          key,
          messages: typeof formData.errors[key] === 'string'? [formData.errors[key]] : [...formData.errors[key]]
        }
          mappedErrors.push(errorItem);
      }
    }
    formData.errors = mappedErrors;
  }

  /**
   * @ngdoc method
   * @name mapExtras
   * @methodOf RaptorDataService
   * @description Maps the extras data.
   * @param {any} data - The data containing the extras to be mapped.
   * @returns {any} The mapped extras data.
   */
  public mapExtras(formData: any, question: any, frequency?: string) {
    const coverFrequency = frequency || formData.data.frequency;
    let coverObj!: any;

    question.settings = question.settings || {};
    // hack for now because the Backend is not enforcing the extras question to have updateOnChange
    if (formData.actions.live && question.settings.updateOnChange !== false) {
      question.settings.updateOnChange = true;
    }
    // find the cover object
    if (Array.isArray(formData.covers)) {
      coverObj = formData.covers && formData.covers.find((cover: any) => {
        return cover.cover.cover_level === formData.data.cover_level &&
          cover.cover.cover_type === formData.data.cover_type &&
          cover.cover.product_slug === formData.data.product_slug;
      });
    } else {
      coverObj = formData.covers;
    }
    if (coverObj && coverObj.extras) {
      // filter out only the extras available on the cover object
      question.settings.choices = question.settings.choices.filter((choice: any) => {
        return coverObj.extras[choice.value];
      });
      // map the extras options
      question.settings.choices.map((choice: any) => {
        let premium;
        const extra = coverObj.extras[choice.value];
        let frequency;

        if (question.settings.hide_price !== true) {
          premium = coverObj.premium[coverFrequency].extras[choice.value];
          if (coverFrequency === 'U') {
            frequency = 'year';
          }
          if (coverFrequency === 'M') {
            frequency = 'month';
          }
          choice.price = {
            frequency: frequency,
            amount: coverFrequency === 'M' ? premium.payable_monthly : premium.payable,
            currency: this.partnerBrandService.getCurrencySymbol()
          };
        }

        // remove frequency label if it is null
        if (
          coverObj.premium[coverFrequency] &&
          coverObj.premium[coverFrequency].frequency_label === null &&
          choice.price
        ) {
          choice.price.frequency = null;
        }

        choice.label = extra.label;
        choice.text = extra.description;
        choice.is_included = extra.is_included; // is selected
        choice.is_optional = extra.is_optional; // is enabled
        choice.inclusion_tag = extra.inclusion_tag; // inclusion tag
        choice.disabled = !choice.is_optional;
      });
    }
  }

  /**
   * @ngdoc method
   * @name mapMonthYearChoices
   * @methodOf RaptorDataService
   * @description Maps dates for month year select.
   * @param {any} data - The data settings to be mapped.
   * @returns {any} The question choices data for month year select.
   */
  mapMonthYearChoices(question: any, value?: any): any {
    const currentDate = new Date();
    // make a copy because we don't want any reference to the previous settings object
    let settings = JSON.parse(JSON.stringify(question.settings)) || {};
    
    let min = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    min.setDate(min.getDate() + settings.min_date);
    let max = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    max.setDate(max.getDate() + settings.max_date);

    // this is when the question has a value and we want to restrict the month options
    if (value) {
      let yearIndex = settings.year.choices.findIndex((item: any) => { return item.value === value.year; })
      
      if (yearIndex === 0) {
        min = new Date(max.getFullYear(), 0, 1);
      }
      if (yearIndex === settings.year.choices.length - 1) {
        max = new Date(min.getFullYear(), 11, 1);
      }
    }

    let choices: any = {
      month: [],
      year: []
    };

    while (max.valueOf() > min.valueOf()) {
      let month = moment(max).format('MM');
      let year = moment(max).format('YYYY');
      let dayOfMonth = max.getDate();
      
      if (!choices.year.find((item: any) => { return item.value === year }) && dayOfMonth === 1) {
        choices.year.push({
          value: year,
          label: year
        });
      }

      if (!choices.month.find((item: any) => { return item.value === month })) {
        choices.month.push({
          value: month,
          label: moment(max).format('MMMM')
        });
      }
  
      max.setDate(max.getDate() - 1);
    }

    if (choices.year.length && !value) {
      settings.year = { choices: choices.year };
    }
    if (choices.month.length) {
      settings.month = { choices: choices.month.sort((a: any, b: any) => a.value - b.value) };
    }

    return settings;
  }

  /**
   * @ngdoc method
   * @name mapPaymentOptions
   * @methodOf RaptorDataService
   * @description Maps the payment options.
   * @param {any} data - The data containing the payment options to be mapped.
   * @returns {any} The mapped payment options data, with overrides if any.
   */
  public mapPaymentOptions(question: any) {
    const paymentOptionsSettings = question.settings.payment_options;
    const overrides = question.settings.payment_options_overrides;

    for (let option of paymentOptionsSettings) {
      const service = option.service;
      option.icon = overrides ? overrides[service].icon : service;
      option.label = overrides ? overrides[service].label : service;
      option.submit_label = overrides && overrides[service].submit_label ? overrides[service].submit_label : null;
      option.pre_submit_label = overrides && overrides[service].pre_submit_label ? overrides[service].pre_submit_label : null;
    }

    return paymentOptionsSettings;
  }

  /**
   * 
   * @ngdoc method
   * @methodOf RaptorDataService
   * @description Maps the conditions value to the medical question and sets updateOnChange to true
   * on the question as a default
   * @param formData the step data
   * @param question the medical question
   * @param data - The data object that holds the question value. this is either the formData.data object
   * or the data object in a repeater question
   */
  mapMedicalQuestion(formData: any, question: any, data: any) {
    let questionKey = question.key;
    let questionData = data;

    if (question.key.indexOf('__') > -1) {
      questionKey = question.key.split('__').pop();
    }
    // this is logic for a mutliple repeater being updated on change
    // Have to strip the injected indexes in the keys and match it with the rows data
    // if the question key has a valid index then it's a repeater and parse the question data
    // and find the conditions list in the data object
    if (questionKey.indexOf('index') > -1 && parseInt(questionKey.split('_').pop().replace('index', ''), 10) > -1) {
      // remove the index from the question key in order to match the data
      questionKey = question.key.split('_').slice(0, -1).join('_');
      questionData = {};
      
      for (let key in data) {
        // remove the indexes off the question data
        let newKey = key.split('_').slice(0, -1).join('_');
        if (newKey) {
          questionData[newKey] = data[key];
        }
      }
    }

    if (questionData && questionData[questionKey + RAPTOR_MEDICAL_VALUE_KEY]) {
      question.conditions = questionData[questionKey + RAPTOR_MEDICAL_VALUE_KEY];
    }

    if (formData.actions?.live) {
      question.settings = question.settings || {};
      question.settings.updateOnChange = true;
    }
  }

  /**
   * @ngdoc method
   * @name isCoverVisible
   * @methodOf RaptorDataService
   * @description Checks to see if a cover should be visible.
   * @returns {boolean} Cover is visible.
   */
  isCoverVisible(cover: any, formData: any, form?: any): boolean {
    let formValues: any = {};
    if (form) {
      formValues = form.value;
    }
    let product_slug = formValues['product_slug'] || formData.data['product_slug'];
    let cover_type = formValues['cover_type'] || formData.data['cover_type'];
    let cover_level = formValues['cover_level'] || formData.data['cover_level'];
    
    // Check if the cover matches the cover_type
    return cover.cover.cover_type === cover_type
  }

  /**
   * @ngdoc method
   * @name getSelectedCover
   * @methodOf RaptorDataService
   * @description Retrieves the selected cover.
   * @returns {any} The selected cover.
   */
  getSelectedCover(formData: any, form?: any) {
    let formValues: any = {};
    if (form) {
      formValues = form.value;
    }
    const product_slug = formValues['product_slug'] || formData.data['product_slug'];
    const cover_type = formValues['cover_type'] || formData.data['cover_type'];
    const cover_level = formValues['cover_level'] || formData.data['cover_level'];

    return formData.covers.find((coverItem: any) => 
      coverItem.cover.product_slug === product_slug &&
      coverItem.cover.cover_type === cover_type &&
      coverItem.cover.cover_level === cover_level
    );
  }

  /**
   * @ngdoc method
   * @name mapBannerDisclaimers
   * @methodOf RaptorDataService
   * @description Maps the banner disclaimers data.
   * @param {any} data - The data containing the banner disclaimers to be mapped.
   * @returns {any} The mapped banner disclaimers data.
   */
  mapBannerDisclaimers(covers: any): Array<IDisclaimer> {
    let disclaimers: Array<any> = [];
    let banners: Array<any> = [];
    let tags = ['*', '†', '‡'];

    if (covers && Array.isArray(covers)) {
      covers.forEach((cover: any) => {
        // Get the promotional banners and disclaimers
        if (cover && cover.banner && cover.banner.length) {
          cover.banner.forEach((banner: any) => {
            banners.push(banner);
            if (banner.disclaimer) {
              disclaimers.push({
                type: 'promotional',
                content: banner.disclaimer
              });
            }
          });
        }

        // Get the additional fees banners and disclaimers
        if (cover && cover.has_additional_charges && cover.additional_fees_disclaimer) {
          disclaimers.push({
            type: 'additionalCharges',
            content: cover.additional_fees_disclaimer
          });
        }
      });
    } else if (covers && typeof covers === 'object' && covers.has_additional_fees && covers.disclaimers.additional_fees_disclaimer) {
      disclaimers.push({ 
        type: 'additionalCharges',
        content: covers.disclaimers.additional_fees_disclaimer
      });
    }

    if (disclaimers.length) {
      disclaimers = Array.from(new Set(disclaimers.map(disclaimer => disclaimer.content)))
        .map(content => {
          return disclaimers.find(disclaimer => disclaimer.content === content);
        });

      disclaimers.forEach((disclaimer: { content: string }, index: number) => {
        disclaimer.content = (index > 2 ? index - 2 : tags[index]) + ' ' + disclaimer.content;
      });
    }

    this.disclaimers = disclaimers;
    this.banners = banners;

    return disclaimers;
  }

  /**
   * @ngdoc method
   * @name mapBannerTag
   * @methodOf RaptorDataService
   * @description Maps the banner tag data.
   * @param {any} data - The data containing the banner tag to be mapped.
   * @returns {any} The mapped banner tag data.
   */
  mapBannerTag(banner: any, disclaimers: any): string {
    let tag = '';

    if (disclaimers && Object.keys(disclaimers).length && banner.tag) {
      disclaimers.forEach((disclaimer: any) => {
        const content = disclaimer.content;

        if (content && banner.disclaimer === content.substring(2)) {
          tag = banner.tag + content.substring(0, 2);
        }
      });
    }

    return tag;
  }

  /**
   * @ngdoc method
   * @name getArrangementFeeBanner
   * @methodOf RaptorDataService
   * @description Retrieves the arrangement fee banner.
   * @returns {any} The arrangement fee banner.
   */
  getArrangementFeeBanner(premium: {[key: string]: any}, disclaimers: Array<any>, frequency: 'M' | 'U', roundUp?: boolean) {
    let arrangementFeeBanner: IArrangementFeeBanner | null = null;
    const additionalChargesDisclaimer = disclaimers?.find((disclaimer: any) => disclaimer.type === 'additionalCharges');
    const arrangFeeExtra = premium[frequency].extras['arrang_fee'];
    const currency = this.partnerBrandService.getCurrencySymbol();
    // TODO: maybe check that the gst exists in the future, but not for now
    const gstRate = this.partnerBrandService.getAttribute('product', 'partner')?.gst_rate;
    const countryCode = this.partnerBrandService.getCountryCode('country_alpha2');

    let annualTag = '';
    let monthlyTaxTag = '';
    annualTag = RAPTOR_ARRANGEMENT_TAG[countryCode];
    monthlyTaxTag = RAPTOR_ARRANGEMENT_TAG[countryCode];

    if (
      premium &&
      arrangFeeExtra &&
      arrangFeeExtra.charge_upfront &&
      disclaimers.length &&
      additionalChargesDisclaimer
    ) {
      
      const monthlyTag = arrangFeeExtra.tag;
      
      const payable = Math.ceil(arrangFeeExtra.payable);
      const label = arrangFeeExtra.label;

      let payableFirstInvoice;

      if (premium.M) {
        payableFirstInvoice = this.raptorPriceService.getPayableFirstInvoice(premium.M, roundUp);
      }
      // The monthlyTag is used to keep track of mapping the PAF banner's tag/disclaimer symbol
      // even if the frequency is Annual and showing a different banner message
      arrangementFeeBanner = {
        monthlyHeading: `First month ${currency}${payableFirstInvoice}, then`,
        disclaimer: additionalChargesDisclaimer.content.substring(2),
        tag: monthlyTag,
      };
      const mappedBannerTag = this.mapBannerTag(arrangementFeeBanner, disclaimers).slice(-2);

      if (arrangFeeExtra?.is_included) {
        arrangementFeeBanner.tag = {
          'monthly': `${monthlyTaxTag}<br/>${monthlyTag} ${currency}${payable} ${label}<sup>${mappedBannerTag}</sup>`,
          'annual': `${annualTag}, and ${currency}${payable} ${label}<sup>${mappedBannerTag}</sup>`
        }
      } else {
        arrangementFeeBanner.tag = {
          'monthly': `${monthlyTaxTag}`,
          'annual': `${annualTag}`
        }
      }
      
    }

    return arrangementFeeBanner;
  }

  /**
   * @ngdoc method
   * @name getIncludedExtrasDisclaimers
   * @methodOf RaptorDataService
   * @description Retrieves the disclaimers for included extras.
   * @returns {any} The disclaimers for included extras.
   */
  getIncludedExtrasDisclaimers(extras: {[key: string]: any}): Array<IIncludedExtrasDisclaimer> {
    let includedExtras: Array<any> = [];

    for (let extra in extras) {
      if (extras[extra].is_included && extras[extra].label && extras[extra].disclaimer) {
        const data = {
          label: extras[extra].label,
          disclaimer: extras[extra].disclaimer
        }

        // Always keep Policy Arrangment Fee at the top
        extra === 'arrang_fee' ? includedExtras.unshift(data) : includedExtras.push(data);
      }
    }

    return includedExtras;
  }

  /**
   *
   * @param formData current step data
   * @param response response from updateOnChange event
   * @description This function updates all the copy on the step via the updateOnChange event without affecting the form object
   */
  public setStepCopy(formData: any, response: any) {
    let that = this;
    let sections = formData.sections;
    let sidebar = formData.sidebar;

    mapPage(formData, response);
    if (sections && sections.length) {
      sections.forEach((section: any, sectionIndex: number) => {
        let responseSection = response.sections[sectionIndex];
        mapSection(section, responseSection);

        if (section.elements && section.elements.length) {
          section.elements.forEach((question: any, questionIndex: number) => {
            let responseQuestion = responseSection.elements[questionIndex];
            if (question && responseQuestion) {
              mapQuestion(question, responseQuestion, response.data, response.human);
              mapSettings(question, responseQuestion);
            }
          });
        }
      });
    }

    if (sidebar) {
      if (sidebar.sections && sidebar.sections.length) {
        sidebar.sections.forEach((section: any, sectionIndex: number) => {
          let responseSidebarSection = response.sidebar.sections[sectionIndex];
          mapSection(section, responseSidebarSection);

          if (section.elements && section.elements.length) {
            section.elements.forEach((question: any, questionIndex: number) => {
              let responseSidebarQuestion = responseSidebarSection.elements[questionIndex];
              if (question && responseSidebarQuestion) {
                mapQuestion(question, responseSidebarQuestion, response.data, response.human);
                mapSettings(question, responseSidebarQuestion);
              }
            });
          }
        });
      }
    }

    function mapPage(formData: any, data: any) {
      formData.heading = data.heading;
      formData.subtext = data.subtext;
      formData.info = data.info;
      formData.disclaimer = data.disclaimer;
      formData.errors = data.errors;
      formData.progress = data.progress;
      formData.total_pages = data.total_pages;
    }

    function mapSection(section: any, data: any) {
      section.banner = data.banner;
      section.heading = data.heading;
      section.subtext = data.subtext;
      section.info = data.info;
      section.disclaimer = data.disclaimer;
    }

    function mapQuestion(question: any, data: any, formData: any, formHuman: any, parentQuestion?: any) {
      let questionIndex = '';
      let questionIndexKey = question.key.split('_').pop();

      if (question.type !== 'repeater') {
        question.prompt = data.prompt;
        question.subtext = data.subtext;
        question.info = data.info;
        question.help = data.help;

        // question index on repeaters should have this structure `index0'
        if (questionIndexKey.indexOf('index') > -1 && parseInt(questionIndexKey.replace('index', ''), 10) > -1) {
          questionIndex = questionIndexKey;
        }

        if (data.settings && formData && formHuman) {
          question.prompt = that.replaceQuestionLabel('prompt', question, formData, formHuman, parentQuestion?.key, questionIndex);
          question.subtext = that.replaceQuestionLabel('subtext', question, formData, formHuman, parentQuestion?.key, questionIndex);
        }
      }
      
      if (question.type === 'medical_screening') {
        that.mapMedicalQuestion(response, question, formData);
      }
      if (question.type === 'repeater' && Array.isArray(question.elements)) {
        // need to figure out what I'm adding here
        let updatedRepeaterData = that.mapRepeaterData(question.key, formData[question.key]);
        let updatedRepeaterHuman = that.mapRepeaterData(question.key, formHuman[question.key]);

        if (question.layout === 'multiple' && updatedRepeaterData && updatedRepeaterData.length) {
          // iterate through each row of question
          question.fields.forEach((elements: any, i: number) => {
            // get the data row 
            let groupData = updatedRepeaterData[i];
            let groupHuman = updatedRepeaterHuman[i];
            
            // get the index of the questions in this row
            questionIndex = elements[0][0].key.split('_').pop();

            // add this question index to each item in the group data
            for (let key in groupData) {
              groupData[key + '_' + questionIndex] = groupData[key];
              groupHuman[key + '_' + questionIndex] = groupHuman[key];
              delete groupData[key];
              delete groupHuman[key];
            }
            // map each of these questions
            elements.forEach((column: any, columnIndex: number) => {
              // maybe I have to find the new question in the response
              column.forEach((element: any) => {
                mapQuestion(element, data.elements[columnIndex], groupData, groupHuman, question);
              });
            });
          });
        } else {
          // TODO: fix this mapping, need to go map the data object which is the values of the question
          question.elements.forEach((element: any, index: number) => {
            // update the question
            // TODO: this needs to be fixed......
            // My thinking is that I'm going to need to build every set of fields on the regular
            // repeater like I do on the multiple repeater and then when editing a row swap out the row of questions
            // that are rendering whilst keeping the original controls, that way the logic here would look like
            // the multiple repeater logic and would probably be more managable.
            // mapQuestion(element, data.elements[index], updatedRepeaterData, updatedRepeaterHuman, question);
          });
        }
      }
    }

    function mapSettings(question: any, data: any) {
      if (question.settings && data.settings) {
        if (question.type === 'extras') {
          that.mapExtras(formData, data);
          question.settings.choices = data.settings.choices;
        } else {
          for (let key in data.settings) {
            if (key === 'choices') {
              data.settings.choices.forEach((choice: any, key: any) => {
                if (question.settings.choices && question.settings.choices[key]) {
                  choice.label = question.settings.choices[key].label;
                  choice.text = question.settings.choices[key].text;
                }
              });
            }
          }
        }
      }
    }
  }
  
  /**
   * 
   * @param label - the question attribute being updated
   * @param question - the question being updated
   * @param data - the data object where the value of the attribute exists if it is a question key
   * @param human - the human data object where the value of the attribute exists if it is a question key, prioritise getting this value first
   * @param parent - if the question is in a repeater then use the parent question key to map to the data object
   * @param index - The index of a row in a repeater array
   * @returns value - the value of the question attribute being returned
   */
  replaceQuestionLabel(label: string, question: any, data: any, human: any, parent?: string, index?: string): string {
    let value = question[label];
    let questionIndex = index ? '_' + index : '';
    // this flag gets set to true if we expect the value to come from a question value in the data
    // If this flag is not set and there is no value, then the label will be the setting value as a string
    let labelAsQuestion = false;
    
    if (question.settings) {
      if (question.settings[label]) {
        if (typeof question.settings[label] === 'string') {
          let questionLabel = question.settings[label];
          if (questionLabel[0] === '{' && questionLabel[questionLabel.length -1] === '}') {
            questionLabel = questionLabel.substring(1, questionLabel.length - 1);
            labelAsQuestion = true;
          }
          let key = parent ? parent + '__' + questionLabel + questionIndex : questionLabel;
          value = human && human[key] || data && data[key] || '';
        }

        if (Array.isArray(question.settings[label])) {
          value = '';
          question.settings[label].forEach((item: string) => {
            let questionLabel = item;
            if (questionLabel[0] === '{' && questionLabel[questionLabel.length - 1] === '}') {
              questionLabel = questionLabel.substring(1, questionLabel.length - 1);
              labelAsQuestion = true;
            }
            // if it's a repeater then the key gets modified to match the data and human object keys
            let key = parent ? parent + '__' + questionLabel + questionIndex : questionLabel;
            // check if item exists on human or data and then push that value on label
            // else push the string value onto label
            if (human[key]) {
              value += human[key];
            } else if (data[key]) {
              value += data[key];
            } else {
              value += questionLabel;
            }
          });
        }
        // if the value was not mapped to a human or data value of a question then just use the original value of the settings
        if (!labelAsQuestion) {
          value = question.settings[label];
        }

        if (Array.isArray(value)) {
          let text = '<ul>';
          value.forEach((item: any) => {
            text += `<li>${item}</li>`;
          });
          text += '</ul>';
          value = text;
        }
      }
    }
    return value;
  }

  filterMobileDetails(countryCodes: Array<string>) {
    let mobileDetails: Array<any> = [];

    if (countryCodes && countryCodes.length) {
      mobileDetails = countryCodes.reduce((countryItem, countryCode) => {
        const country = RAPTOR_COUNTRY_CODES[countryCode as keyof typeof RAPTOR_COUNTRY_CODES];
        if (country) {
          countryItem.push({ ...country, country_alpha3: countryCode });
        }
        return countryItem;
      }, [] as Array<any>);
    }
    
    return mobileDetails;
  }
}
